/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { Configuration, LogLevel, RedirectRequest } from "@azure/msal-browser";

import { GetB2CPolicyInfo } from "./b2cPolicies";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export function getConfig(): Configuration {
  const REACT_APP_MSAL_CLIENT_ID =
    (window as any)["runConfig"]?.REACT_APP_MSAL_CLIENT_ID ??
    (process.env.REACT_APP_MSAL_CLIENT_ID as string);
  const REACT_APP_BASEURL =
    (window as any)["runConfig"]?.REACT_APP_BASEURL ??
    (process.env.REACT_APP_BASEURL as string);
  // new configs for B2C : these are drawn from SPA Configs
  const b2cPolicies = GetB2CPolicyInfo();

  return {
    auth: {
      clientId: REACT_APP_MSAL_CLIENT_ID,
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      knownAuthorities: [b2cPolicies.authorityDomain],
      redirectUri: REACT_APP_BASEURL,
      postLogoutRedirectUri: REACT_APP_BASEURL,
    },
    cache: {
      cacheLocation: "sessionStorage", // This configures where your cache will be stored
      storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              console.error(message);
              return;
          }
        },
      },
    },
  };
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: RedirectRequest = {
  scopes: ["openid", "offline_access"],
};

export function forgotPasswordRequest(): RedirectRequest {
  return {
    scopes: [
      "openid",
      "offline_access",
      (window as any)["runConfig"]?.REACT_APP_BFF_API_SCOPES ??
        (process.env.REACT_APP_BFF_API_SCOPES as string),
    ],
    authority:
      ((window as any)["runConfig"]?.REACT_APP_MSAL_POLICY_AUTHORITY_ROOT ??
        (process.env.REACT_APP_MSAL_POLICY_AUTHORITY_ROOT as string)) +
      ((window as any)["runConfig"]?.REACT_APP_MSAL_FORGOTPASSWORD_POLICY ??
        (process.env.REACT_APP_MSAL_FORGOTPASSWORD_POLICY as string)),
  };
}

export function getScopes(): string[] {
  return [
    "openid",
    "offline_access",
    (window as any)["runConfig"]?.REACT_APP_BFF_API_SCOPES ??
      (process.env.REACT_APP_BFF_API_SCOPES as string),
  ];
}

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};
